import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toWebinarLng } from '@/lib/webinarLanguage';
import { toCtaButtonLabelInput } from '@/lib/ctaButtonLabel';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import PreviewAttendeeSignin from '@/components/pages/webinars/resources/visual-identity/PreviewAttendeeSignin';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchWebinarSubscriptionPage,
  updateWebinarSubscriptionPage,
  uploadWebinarSubscriptionPageBackgroundImage,
} from '@/features/webinar/webinarThunks';
import { Button } from '@/components/ui/button';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';

interface SigninSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}
const CTA_BUTTON_LABEL_MAX_LENGTH = 50;

export default function SigninSection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: SigninSectionProps) {
  const { t } = useTranslation('createWebinar');
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const { currentWebinarSubscriptionPage, subscriptionPageLoading, currentWebinar } =
    useAppSelector((state: RootState) => state.webinar);
  const isEvergreen = currentWebinar?.type === 'EVERGREEN';
  const webinarId = webinarIdParam || currentWebinarId;
  const [displayLogo, setDisplayLogo] = useState(false);
  const [displaySpeakers, setDisplaySpeakers] = useState(false);
  const [lastNameField, setLastNameField] = useState(false);
  const [lastNameMandatory, setLastNameMandatory] = useState(false);
  const [phoneField, setPhoneField] = useState(false);
  const [phoneMandatory, setPhoneMandatory] = useState(false);
  const [background, setBackground] = useState<'DEFAULT' | 'GRADIENT' | 'IMAGE'>('DEFAULT');
  const [selectedGradient, setSelectedGradient] = useState<string>('1');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const { t: tWebinarLng } = useTranslation('webinars', {
    lng: currentWebinar ? toWebinarLng(currentWebinar.language) : undefined,
  });
  const defaultSubmitButtonLabel = tWebinarLng('publicSignin.register');
  const [submitButtonLabel, setSubmitButtonLabel] = useState('');
  const [accordionValue, setAccordionValue] = useState<string[]>(
    !isEdit && !isEvergreen ? ['item-1', 'item-3'] : [],
  );
  const fields = [
    { id: 'firstname', name: 'firstname', label: t('signinSection.fields.firstName') },
    { id: 'email', name: 'email', label: t('signinSection.fields.email') },
    { id: 'lastname', name: 'lastname', label: t('signinSection.fields.lastName') },
    { id: 'phone', name: 'phone', label: t('signinSection.fields.phone') },
  ];

  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  useEffect(() => {
    if (webinarId && workspaceId) {
      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (!isNaN(id)) {
        dispatch(fetchWebinarSubscriptionPage({ workspaceId, webinarId: id }));
      }
    }
  }, [webinarId, workspaceId, dispatch]);

  useEffect(() => {
    if (isEdit || !isEvergreen) return;
    setAccordionValue([]);
  }, [isEdit, isEvergreen]);

  useEffect(() => {
    if (currentWebinarSubscriptionPage) {
      setDisplayLogo(currentWebinarSubscriptionPage.displayLogo);
      setDisplaySpeakers(currentWebinarSubscriptionPage.displaySpeakers);
      setLastNameField(currentWebinarSubscriptionPage.lastNameField);
      setLastNameMandatory(currentWebinarSubscriptionPage.lastNameMandatory);
      setPhoneField(currentWebinarSubscriptionPage.phoneField);
      setPhoneMandatory(currentWebinarSubscriptionPage.phoneMandatory);
      setBackground(currentWebinarSubscriptionPage.background);
      if (currentWebinarSubscriptionPage.backgroundGradient) {
        setSelectedGradient(currentWebinarSubscriptionPage.backgroundGradient);
      }
      setSubmitButtonLabel(currentWebinarSubscriptionPage.ctaButtonLabel ?? '');
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
        setPreviewImageUrl(null);
      }
      setSelectedImageFile(null);
    }
  }, [currentWebinarSubscriptionPage]);

  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);

  const handleBackgroundChange = useCallback((value: string) => {
    const bgValue = value.toUpperCase() as 'DEFAULT' | 'GRADIENT' | 'IMAGE';
    setBackground(bgValue);

    if (bgValue === 'GRADIENT') {
      setSelectedGradient('1');
    } else if (bgValue === 'DEFAULT') {
      setSelectedGradient('1');
    }
  }, []);

  const handleDisplayLogoChange = useCallback((checked: boolean) => {
    setDisplayLogo(checked);
  }, []);

  const handleDisplaySpeakersChange = useCallback((checked: boolean) => {
    setDisplaySpeakers(checked);
  }, []);

  const handleLastNameFieldChange = useCallback((checked: boolean) => {
    setLastNameField(checked);
    if (!checked) {
      setLastNameMandatory(false);
    }
  }, []);

  const handleLastNameMandatoryChange = useCallback((checked: boolean) => {
    setLastNameMandatory(checked);
  }, []);

  const handlePhoneFieldChange = useCallback((checked: boolean) => {
    setPhoneField(checked);
    if (!checked) {
      setPhoneMandatory(false);
    }
  }, []);

  const handlePhoneMandatoryChange = useCallback((checked: boolean) => {
    setPhoneMandatory(checked);
  }, []);

  const handleGradientChange = useCallback((value: string) => {
    setSelectedGradient(value);
  }, []);

  const getCtaButtonLabelInput = useCallback(
    () => toCtaButtonLabelInput(submitButtonLabel),
    [submitButtonLabel],
  );

  const savedCtaButtonLabel = currentWebinarSubscriptionPage?.ctaButtonLabel ?? '';
  const isCtaAccordionFilled =
    savedCtaButtonLabel !== '' || submitButtonLabel.trim() !== savedCtaButtonLabel;

  const validateFile = useCallback(
    (file: File): boolean => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert(t('signinSection.errors.unsupportedFormat'));
        return false;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(t('signinSection.errors.fileTooLarge'));
        return false;
      }

      return true;
    },
    [t],
  );

  const handleFileUpload = useCallback(
    (file: File) => {
      if (!validateFile(file)) {
        return;
      }

      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }

      setSelectedImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreviewImageUrl(previewUrl);
      setBackground('IMAGE');
    },
    [validateFile, previewImageUrl],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  const handleSubmitFields = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!webinarId || !workspaceId || !currentWebinarSubscriptionPage) {
        return;
      }
      console.log('submit fields');

      setIsSubmitting(true);
      try {
        const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
        if (isNaN(id)) {
          return;
        }
        const page = currentWebinarSubscriptionPage;
        const finalBackgroundColor =
          page.backgroundColor && page.backgroundColor.trim() !== ''
            ? page.backgroundColor
            : visualIdentity?.backgroundColor || '#FBFBFB';
        await dispatch(
          updateWebinarSubscriptionPage({
            workspaceId,
            webinarId: id,
            input: {
              firstNameField: page.firstNameField,
              lastNameField: lastNameField,
              emailField: page.emailField,
              phoneField: phoneField,
              firstNameMandatory: page.firstNameMandatory,
              lastNameMandatory: lastNameMandatory,
              emailMandatory: page.emailMandatory,
              phoneMandatory: phoneMandatory,
              displayLogo: displayLogo,
              displaySpeakers: displaySpeakers,
              ctaButtonLabel: getCtaButtonLabelInput(),
              background: page.background,
              backgroundColor: finalBackgroundColor,
              backgroundGradient: page.backgroundGradient ?? null,
              inputModified: true,
              backgroundModified: page.backgroundModified,
            },
          }),
        ).unwrap();
        toast.success(t('signinSection.saveSuccess'));
        setAccordionValue((prev) => [...prev.filter((v) => v !== 'item-1'), 'item-cta']);
      } catch (error) {
        const appError = error as AppError | undefined;
        if (appError?.message) {
          const mappedMessage = mapHttpError(appError.message);
          const translatedMessage = t(mappedMessage, appError.message);
          toast.error(translatedMessage);
        } else {
          toast.error(t('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
        }
        if (appError?.details && Array.isArray(appError.details)) {
          appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedMessage = t(mappedMessage, detail.message);
              toast.error(translatedMessage);
            }
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      webinarId,
      workspaceId,
      currentWebinarSubscriptionPage,
      dispatch,
      lastNameField,
      lastNameMandatory,
      phoneField,
      phoneMandatory,
      displayLogo,
      displaySpeakers,
      visualIdentity,
      getCtaButtonLabelInput,
      t,
    ],
  );

  const handleSubmitCta = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!webinarId || !workspaceId || !currentWebinarSubscriptionPage) {
        return;
      }

      setIsSubmitting(true);
      try {
        const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
        if (isNaN(id)) {
          return;
        }
        const page = currentWebinarSubscriptionPage;
        const finalBackgroundColor =
          page.backgroundColor && page.backgroundColor.trim() !== ''
            ? page.backgroundColor
            : visualIdentity?.backgroundColor || '#FBFBFB';
        await dispatch(
          updateWebinarSubscriptionPage({
            workspaceId,
            webinarId: id,
            input: {
              firstNameField: page.firstNameField,
              lastNameField: lastNameField,
              emailField: page.emailField,
              phoneField: phoneField,
              firstNameMandatory: page.firstNameMandatory,
              lastNameMandatory: lastNameMandatory,
              emailMandatory: page.emailMandatory,
              phoneMandatory: phoneMandatory,
              displayLogo: displayLogo,
              displaySpeakers: displaySpeakers,
              ctaButtonLabel: getCtaButtonLabelInput(),
              background: background,
              backgroundColor: finalBackgroundColor,
              backgroundGradient:
                background === 'GRADIENT'
                  ? selectedGradient
                  : background === 'DEFAULT'
                    ? null
                    : page.backgroundGradient,
              inputModified: page.inputModified,
              backgroundModified: page.backgroundModified,
            },
          }),
        ).unwrap();
        toast.success(t('signinSection.saveSuccess'));
        setAccordionValue((prev) => [...prev.filter((v) => v !== 'item-cta'), 'item-2']);
      } catch (error) {
        const appError = error as AppError | undefined;
        if (appError?.message) {
          const mappedMessage = mapHttpError(appError.message);
          const translatedMessage = t(mappedMessage, appError.message);
          toast.error(translatedMessage);
        } else {
          toast.error(t('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
        }
        if (appError?.details && Array.isArray(appError.details)) {
          appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedMessage = t(mappedMessage, detail.message);
              toast.error(translatedMessage);
            }
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      webinarId,
      workspaceId,
      currentWebinarSubscriptionPage,
      dispatch,
      lastNameField,
      lastNameMandatory,
      phoneField,
      phoneMandatory,
      displayLogo,
      displaySpeakers,
      background,
      selectedGradient,
      visualIdentity,
      getCtaButtonLabelInput,
      t,
    ],
  );

  const handleSubmitBackground = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!webinarId || !workspaceId || !currentWebinarSubscriptionPage) {
        return;
      }
      console.log('submit   background');

      setIsSubmitting(true);
      try {
        const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
        if (isNaN(id)) {
          return;
        }

        const newBackground = background;
        const finalBackgroundColor =
          newBackground === 'DEFAULT' &&
          (!currentWebinarSubscriptionPage.backgroundColor ||
            currentWebinarSubscriptionPage.backgroundColor.trim() === '')
            ? visualIdentity?.backgroundColor || '#FBFBFB'
            : currentWebinarSubscriptionPage.backgroundColor;

        if (selectedImageFile && newBackground === 'IMAGE') {
          await dispatch(
            updateWebinarSubscriptionPage({
              workspaceId,
              webinarId: id,
              input: {
                firstNameField: currentWebinarSubscriptionPage.firstNameField,
                lastNameField: lastNameField,
                emailField: currentWebinarSubscriptionPage.emailField,
                phoneField: phoneField,
                firstNameMandatory: currentWebinarSubscriptionPage.firstNameMandatory,
                lastNameMandatory: lastNameMandatory,
                emailMandatory: currentWebinarSubscriptionPage.emailMandatory,
                phoneMandatory: phoneMandatory,
                displayLogo: displayLogo,
                displaySpeakers: displaySpeakers,
                ctaButtonLabel: getCtaButtonLabelInput(),
                background: 'IMAGE',
                backgroundColor: finalBackgroundColor,
                backgroundGradient: null,
                backgroundModified: true,
              },
            }),
          ).unwrap();

          setIsUploading(true);
          try {
            await dispatch(
              uploadWebinarSubscriptionPageBackgroundImage({
                workspaceId,
                webinarId: id,
                file: selectedImageFile,
              }),
            ).unwrap();
          } catch (error) {
            const appError = error as AppError | undefined;

            if (appError?.message) {
              const mappedMessage = mapHttpError(appError.message);
              const translatedMessage = t(mappedMessage, appError.message);
              toast.error(translatedMessage);
            } else {
              toast.error(
                t('signinSection.errors.uploadError', "Erreur lors de l'upload de l'image."),
              );
            }

            if (appError?.details && Array.isArray(appError.details)) {
              appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
                if (detail.message) {
                  const mappedMessage = mapHttpError(detail.message);
                  const translatedMessage = t(mappedMessage, detail.message);
                  toast.error(translatedMessage);
                }
              });
            }

            setIsUploading(false);
            setIsSubmitting(false);
            return;
          } finally {
            setIsUploading(false);
          }
        } else if (!selectedImageFile) {
          await dispatch(
            updateWebinarSubscriptionPage({
              workspaceId,
              webinarId: id,
              input: {
                firstNameField: currentWebinarSubscriptionPage.firstNameField,
                lastNameField: lastNameField,
                emailField: currentWebinarSubscriptionPage.emailField,
                phoneField: phoneField,
                firstNameMandatory: currentWebinarSubscriptionPage.firstNameMandatory,
                lastNameMandatory: lastNameMandatory,
                emailMandatory: currentWebinarSubscriptionPage.emailMandatory,
                phoneMandatory: phoneMandatory,
                displayLogo: displayLogo,
                displaySpeakers: displaySpeakers,
                ctaButtonLabel: getCtaButtonLabelInput(),
                background: newBackground,
                backgroundColor: finalBackgroundColor,
                backgroundGradient:
                  newBackground === 'GRADIENT'
                    ? selectedGradient
                    : newBackground === 'DEFAULT'
                      ? null
                      : currentWebinarSubscriptionPage.backgroundGradient,
                backgroundModified: true,
              },
            }),
          ).unwrap();
        }

        if (selectedImageFile && previewImageUrl) {
          URL.revokeObjectURL(previewImageUrl);
          setSelectedImageFile(null);
          setPreviewImageUrl(null);
        }

        toast.success(t('signinSection.saveSuccess'));
        if (!isEdit) {
          navigate(`/create-webinar/waiting-room/${id}`);
        } else if (isEdit && onSaveSuccess) {
          window.scrollTo({ top: 0, behavior: 'smooth' });

          onSaveSuccess();
        }
      } catch (error) {
        const appError = error as AppError | undefined;

        if (appError?.message) {
          const mappedMessage = mapHttpError(appError.message);
          const translatedMessage = t(mappedMessage, appError.message);
          toast.error(translatedMessage);
        } else {
          toast.error(t('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
        }

        if (appError?.details && Array.isArray(appError.details)) {
          appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedMessage = t(mappedMessage, detail.message);
              toast.error(translatedMessage);
            }
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      webinarId,
      workspaceId,
      currentWebinarSubscriptionPage,
      dispatch,
      lastNameField,
      lastNameMandatory,
      phoneField,
      phoneMandatory,
      displayLogo,
      displaySpeakers,
      background,
      selectedGradient,
      visualIdentity,
      selectedImageFile,
      previewImageUrl,
      getCtaButtonLabelInput,
      t,
      navigate,
      isEdit,
      onSaveSuccess,
    ],
  );

  if (subscriptionPageLoading && !currentWebinarSubscriptionPage) {
    return (
      <TitleLayout
        title={t('signinSection.title')}
        subtitle={t('signinSection.subtitle')}
        description={t('signinSection.description')}
      >
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-64" />
            <Skeleton className="h-6 w-12 rounded-full" />
          </div>

          <div className="space-y-4 rounded-lg p-4">
            <Skeleton className="h-5 w-40" />
            {[1, 2, 3, 4].map((item) => (
              <div
                key={`signin-fields-skeleton-${item}`}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-12 rounded-full" />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4 rounded-lg p-4">
            <Skeleton className="h-5 w-56" />
            <div className="flex flex-wrap items-center gap-6">
              {[1, 2, 3].map((item) => (
                <div key={`signin-background-radio-${item}`} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ))}
            </div>
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-4 rounded-lg p-4">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      </TitleLayout>
    );
  }

  const currentBackgroundImage =
    previewImageUrl ||
    (currentWebinarSubscriptionPage?.backgroundImageUrl
      ? decodeURIComponent(currentWebinarSubscriptionPage.backgroundImageUrl)
      : null);

  return (
    <TitleLayout
      isEdit={isEdit}
      title={t('signinSection.title')}
      subtitle={t('signinSection.subtitle')}
      description={t('signinSection.description')}
    >
      <div className="space-y-8">
        {visualIdentity?.logoUrl && (
          <div className="flex justify-between items-center">
            <Label>{t('signinSection.displayLogo')}</Label>
            <Switch checked={displayLogo} onCheckedChange={handleDisplayLogoChange} />
          </div>
        )}
        <div className="flex justify-between items-center">
          <Label>{t('signinSection.displaySpeakers')}</Label>
          <Switch checked={displaySpeakers} onCheckedChange={handleDisplaySpeakersChange} />
        </div>

        <Accordion
          type="multiple"
          className={cn('w-full', isEdit && 'space-y-8')}
          value={accordionValue}
          onValueChange={setAccordionValue}
        >
          <AccordionItem
            value="item-1"
            className={cn(isEdit && 'accordion-page')}
            hideSeparator={isEdit}
          >
            <AccordionTrigger fillForm={currentWebinarSubscriptionPage?.inputModified}>
              {t('signinSection.fields.title')}
            </AccordionTrigger>
            <AccordionContent>
              <form onSubmit={handleSubmitFields} className="space-y-4">
                {fields.map((field) => {
                  const isDisabled = field.id === 'firstname' || field.id === 'email';
                  const isLastName = field.id === 'lastname';
                  const isPhone = field.id === 'phone';

                  let checked = false;
                  let mandatory = false;
                  let onFieldChange: ((checked: boolean) => void) | undefined;
                  let onMandatoryChange: ((checked: boolean) => void) | undefined;

                  if (isLastName) {
                    checked = lastNameField;
                    mandatory = lastNameMandatory;
                    onFieldChange = handleLastNameFieldChange;
                    onMandatoryChange = handleLastNameMandatoryChange;
                  } else if (isPhone) {
                    checked = phoneField;
                    mandatory = phoneMandatory;
                    onFieldChange = handlePhoneFieldChange;
                    onMandatoryChange = handlePhoneMandatoryChange;
                  }

                  return (
                    <div key={field.id} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={field.id}
                          name={field.name}
                          value={field.name}
                          checked={isDisabled ? true : checked}
                          disabled={isDisabled}
                          onCheckedChange={onFieldChange}
                        />
                        <Label htmlFor={field.id} className="font-normal">
                          {field.label}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label>{t('signinSection.fields.mandatory')}</Label>
                        <Switch
                          checked={isDisabled ? true : mandatory}
                          disabled={
                            isDisabled || (isLastName && !lastNameField) || (isPhone && !phoneField)
                          }
                          onCheckedChange={onMandatoryChange}
                        />
                      </div>
                    </div>
                  );
                })}
                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </form>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-cta"
            className={cn(isEdit && 'accordion-page')}
            hideSeparator={isEdit}
          >
            <AccordionTrigger fillForm={isCtaAccordionFilled}>
              {t('signinSection.cta.title')}
            </AccordionTrigger>
            <AccordionContent>
              <form onSubmit={handleSubmitCta} className="space-y-4">
                <TextFieldWithLimit
                  label={t('signinSection.cta.buttonLabel')}
                  className="bg-white"
                  value={submitButtonLabel}
                  placeholder={defaultSubmitButtonLabel}
                  maxLength={CTA_BUTTON_LABEL_MAX_LENGTH}
                  onChange={(v) => setSubmitButtonLabel(v)}
                />
                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </form>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-2"
            className={cn(isEdit && 'accordion-page')}
            hideSeparator={isEdit}
          >
            <AccordionTrigger fillForm={currentWebinarSubscriptionPage?.backgroundModified}>
              {t('signinSection.background.title')}
            </AccordionTrigger>
            <AccordionContent>
              <form onSubmit={handleSubmitBackground} className="space-y-4">
                <RadioGroup
                  value={background.toLowerCase()}
                  onValueChange={handleBackgroundChange}
                  className="space-x-6 flex items-center"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="default" />
                    <Label className="font-normal">{t('signinSection.background.default')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="gradient" />
                    <Label className="font-normal">{t('signinSection.background.gradient')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="image" />
                    <Label className="font-normal">{t('signinSection.background.image')}</Label>
                  </div>
                </RadioGroup>

                {background === 'GRADIENT' && visualIdentity && (
                  <div className="relative">
                    <Select value={selectedGradient} onValueChange={handleGradientChange}>
                      <SelectTrigger label={t('signinSection.background.color')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-4 rounded border border-gray-200 shrink-0"
                              style={{
                                background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`,
                              }}
                            />
                            <span>{t('signinSection.background.gradient1')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="2">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-4 rounded border border-gray-200 shrink-0"
                              style={{
                                background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`,
                              }}
                            />
                            <span>{t('signinSection.background.gradient2')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {background === 'IMAGE' && (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`rounded-lg p-4 border-dashed border-2 transition-colors ${
                      isDragging
                        ? 'border-primary bg-primary/5'
                        : 'border-card hover:border-primary/50'
                    } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {/* input sr-only + label htmlFor : ouverture native du sélecteur,
                        sans .click() JS (qui n'ouvrait pas le sélecteur ici). */}
                    <input
                      id="signin-background-input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileInputChange}
                      className="sr-only"
                    />
                    {currentBackgroundImage ? (
                      <div className="flex items-center gap-4">
                        <img
                          src={currentBackgroundImage}
                          alt={t('signinSection.background.selectedFile')}
                          className="h-16 w-16 shrink-0 rounded-md border border-card object-cover"
                        />
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                          <span className="truncate text-sm">
                            {selectedImageFile?.name ?? t('signinSection.background.selectedFile')}
                          </span>
                        </div>
                        <Button
                          asChild
                          variant="outline"
                          className="text-back border-black cursor-pointer shrink-0"
                        >
                          <label htmlFor="signin-background-input">
                            {t('signinSection.background.replace')}
                          </label>
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <Label className="text-secondary font-normal">
                          {isUploading
                            ? t('signinSection.background.uploading')
                            : t('signinSection.background.dragAndDrop')}
                        </Label>
                        <Label className="text-secondary font-normal">
                          {t('signinSection.background.or')}
                        </Label>
                        <Button
                          asChild
                          variant="outline"
                          className="text-back border-black cursor-pointer"
                        >
                          <label htmlFor="signin-background-input">
                            {t('signinSection.background.chooseFile')}
                          </label>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </form>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-3"
            className={cn(isEdit && 'accordion-page')}
            hideSeparator={isEdit}
          >
            <AccordionTrigger>{t('signinSection.preview')}</AccordionTrigger>
            <AccordionContent>
              {isUploading ? (
                <Skeleton className="h-90 w-full rounded-xl" />
              ) : (
                visualIdentity && (
                  <PreviewAttendeeSignin
                    visualIdentity={visualIdentity}
                    theme={visualIdentity.theme || 'LIGHT'}
                    accentColor={visualIdentity.accentColor || '#2EFFB8'}
                    backgroundColor={
                      background === 'GRADIENT' && selectedGradient
                        ? selectedGradient === '1'
                          ? `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`
                          : `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`
                        : visualIdentity.backgroundColor || '#FBFBFB'
                    }
                    contrastColor={visualIdentity.contrastColor || '#2C6CFF'}
                    backgroundImage={
                      background === 'IMAGE'
                        ? previewImageUrl ||
                          (currentWebinarSubscriptionPage?.backgroundImageUrl
                            ? decodeURIComponent(currentWebinarSubscriptionPage.backgroundImageUrl)
                            : undefined)
                        : undefined
                    }
                    displayLogo={displayLogo}
                    displaySpeakers={displaySpeakers}
                    firstNameField={currentWebinarSubscriptionPage?.firstNameField ?? true}
                    lastNameField={lastNameField}
                    emailField={currentWebinarSubscriptionPage?.emailField ?? true}
                    phoneField={phoneField}
                    firstNameMandatory={currentWebinarSubscriptionPage?.firstNameMandatory ?? true}
                    lastNameMandatory={lastNameMandatory}
                    emailMandatory={currentWebinarSubscriptionPage?.emailMandatory ?? true}
                    phoneMandatory={phoneMandatory}
                    submitButtonLabel={getCtaButtonLabelInput() ?? undefined}
                    webinar={currentWebinar}
                  />
                )
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </TitleLayout>
  );
}
